import React, { useState, useCallback, useEffect, useRef  } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../../lib/intersectionObserverPolyfill'; 

import { logsService } from '../../Services';
import { itemsPerPage } from '../../Services'

import {ReactComponent as Plus} from '../../images/icons/General/plus.svg';
import {ReactComponent as Edit} from '../../images/icons/General/edit-01.svg';

export const Incidents = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [info, setInfo] = useState([]);
    const [number, setNumber] = useState(0);
    const [sortField, setSortField] = useState('IncidentDate');
    const [sortOrder, setSortOrder] = useState(false);
    const orgName = useSelector((state) => state.org.name);
    const [page, setPage] = useState(-1);
    const [hasMore, setHasMore] = useState(true);
    const loaderRef = useRef(null);
    const tableRef = useRef(null);
    const initialPageRef = useRef(Math.max(parseInt(searchParams.get('page')) || 1, 1) - 1);
    const firstLoadRef = useRef(true);
    const scrollToIndexRef = useRef(null);

    const resetValues = () => {
        setInfo([]);
        setPage(-1);
        setHasMore(true);
        setNumber(0);
    }

    const sort = (e) => {
        if (e.target.id === sortField)
            setSortOrder(!sortOrder)
        else 
            setSortField(e.target.id);
    
        resetValues();
    }

    const fetchData = useCallback(async (pageNum, pagesCount, sortFieldValue, sortOrderValue) => {
        const res = await logsService.getIncidents(pageNum, sortFieldValue, sortOrderValue, pagesCount);
        setInfo(prev => [...prev, ...res.list]);

        if (pagesCount > 1)
            scrollToIndexRef.current = itemsPerPage * (pagesCount - 1);

        if (res.number)
            setNumber(res.number);

        if (res.list.length < itemsPerPage * pagesCount)
            setHasMore(false);
      }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
        (entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setPage((prev) => prev === -1 && firstLoadRef.current ? initialPageRef.current : prev + 1);
            }
        },
        { threshold: 0.1 }
        );

        const loader = loaderRef.current;
        if (loader) observer.observe(loader);

        return () => {
            if (loader) observer.unobserve(loader);
        };
    }, [hasMore]);

    useEffect(() => {
        if (page < 0) return;
        // on the first load fetch all pages up to the one from the url in one request
        const pagesCount = firstLoadRef.current ? page + 1 : 1;
        firstLoadRef.current = false;
        fetchData(page + 1 - pagesCount, pagesCount, sortField, sortOrder);
    }, [fetchData, page, sortField, sortOrder]);

    useEffect(() => {
        if (page < 0) return;
        const urlPage = page > 0 ? String(page + 1) : null;
        if (searchParams.get('page') === urlPage) return;
        const newParams = new URLSearchParams(searchParams);
        if (urlPage)
            newParams.set('page', urlPage);
        else
            newParams.delete('page');
        setSearchParams(newParams, { replace: true });
    }, [page, searchParams, setSearchParams]);

    useEffect(() => {
        const index = scrollToIndexRef.current;
        if (index === null) return;
        scrollToIndexRef.current = null;
        const row = tableRef.current?.tBodies[0]?.rows[index];
        if (row)
            row.scrollIntoView({ block: 'start' });
    }, [info]);
    
    return (
        <>
            <h1>Log incidents</h1>
            <div className="bordered-content">
                <div className="header">
                    <h4>{number} item{number === 1 ? '' : 's'}</h4>
                    <p>Log a new incident internally at {orgName}</p>
                    <div className="buttons">
                        <Link to="/logs/incidents/add"><button className="primary" type="button"><Plus />Log a new incident</button></Link>
                    </div>
                </div>
                <table ref={tableRef}>
                    <thead>
                        <tr>
                            <th style={{width: '90%'}} className={`sortable ${sortField === 'IncidentDate' ? (sortOrder ? 'down' : 'up') : ''}`} id="IncidentDate" onClick={sort}>Date</th>
                            <th style={{width: '10%'}} className="light">Edit</th>  
                        </tr>
                    </thead>
                    <tbody>
                        {info.length ? info.map(item => {
                            return (
                                <tr key={item.IncidentID}>
                                    <td className="desktop">{item.date}</td>
                                    <td className="mobile"><Link to={`/logs/incidents/${item.IncidentID}`}>{item.date}</Link></td>
                                    <td className="desktop last"><Link to={`/logs/incidents/${item.IncidentID}`}><Edit className="pointer" /></Link></td>
                                </tr>
                            )}
                        ) : (hasMore ? <></> : <tr><td colSpan="9" className="load-more">No dives found</td></tr>)}
                        {hasMore && <tr ref={loaderRef}><td colSpan="9" className="load-more">Loading more...</td></tr>}
                    </tbody>
                </table>
            </div>
            <div className="details">
                <h4>Submitting incidents to AAUS</h4>
                <p>Incidents can now be entered at any point throughout the year by accessing <a href="https://www.aaus.org/" target="_blank" rel="noreferrer">the Statistics tab on the Organizational Member's profile of the main AAUS website</a>.</p>
                <p>Please be sure that the number of incidents entered throughout the calendar year equals the number submitted on your annual statistic form.To enter an incident, select the + sign to the right of the Incidents Entered section and complete the form.</p>
                <p>Please refer to Submission Information (right menu bar or Members tab) for detailed instructions on form completion. Once an incident is submitted the Statistics Chair will be notified for approval.</p>
            </div>
        </>
    );
};

export default Incidents;